import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, Rocket, ArrowRight, Sparkles, Gift } from "lucide-react";
import api from "@/lib/api";
import ConfettiBurst from "@/components/ConfettiBurst";
import ShareTaxSavingsModal from "@/components/ShareTaxSavingsModal";

const CELEBRATE_KEY = "cashflow.onboarding.celebrated";

/**
 * Onboarding checklist card shown on the Dashboard.
 * Auto-hides when 100% complete AND dismissed, or when the user manually
 * dismisses it. Progress is derived server-side from real user data.
 * When the user completes all 5 steps for the first time, a confetti burst
 * and "Invite a teammate" nudge appear that opens the ShareTaxSavingsModal.
 */
export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [taxSaved, setTaxSaved] = useState(0);

  useEffect(() => {
    api
      .get("/dashboard/onboarding")
      .then((r) => {
        setData(r.data);
        if (r.data.complete && !localStorage.getItem(CELEBRATE_KEY)) {
          setShowCelebration(true);
          // Pull the actual tax savings figure to power the share card
          api
            .get("/tax/summary")
            .then((tr) => setTaxSaved(tr.data?.tax_savings_from_deductions || 0))
            .catch(() => {});
        }
      })
      .catch(() => setData(null));
  }, []);

  if (!data || hidden || (data.dismissed_at && !showCelebration)) return null;

  const dismiss = async () => {
    setHidden(true);
    if (showCelebration) localStorage.setItem(CELEBRATE_KEY, "1");
    try {
      await api.post("/dashboard/onboarding/dismiss");
    } catch (e) {
      // Non-critical; UI already hidden.
    }
  };

  const openShare = () => {
    localStorage.setItem(CELEBRATE_KEY, "1");
    setShareOpen(true);
  };

  const nextStep = data.steps.find((s) => !s.done);

  return (
    <>
      <div
        className={`card-surface p-6 mb-6 border-l-4 relative overflow-hidden ${
          showCelebration ? "border-l-ochre bg-forest/[0.02]" : "border-l-forest"
        }`}
        data-testid="onboarding-checklist"
      >
        {showCelebration && <ConfettiBurst />}

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss onboarding checklist"
          className="absolute top-4 right-4 text-ink-lighter hover:text-ink transition-colors z-10"
          data-testid="onboarding-dismiss-btn"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>

        <div className="flex items-start gap-3 mb-4 relative">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              showCelebration ? "bg-ochre text-white" : "bg-forest text-white"
            }`}
          >
            {showCelebration ? (
              <Sparkles className="w-4 h-4" strokeWidth={1.75} />
            ) : (
              <Rocket className="w-4 h-4" strokeWidth={1.75} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-0.5">
              {showCelebration ? "You did it" : "Get set up"}
            </div>
            <div
              className="font-heading text-lg font-medium"
              data-testid="onboarding-headline"
            >
              {showCelebration
                ? "You're all set — invite a teammate?"
                : "Finish setting up BizCashflow Pros"}
            </div>
            <p className="text-sm text-ink-light mt-1">
              {showCelebration
                ? "Every core step is done. Share BizCashflow Pros with a friend — you both get a free month."
                : "A few quick steps unlock the full power of your dashboard."}
            </p>
          </div>
        </div>

        <div className="mb-4 relative">
          <div className="flex items-baseline justify-between mb-1.5 text-xs text-ink-light">
            <span data-testid="onboarding-progress-label">
              <span className="num font-medium text-ink">{data.done}</span> of{" "}
              <span className="num">{data.total}</span> complete
            </span>
            <span className="num font-medium" data-testid="onboarding-percent">
              {data.percent}%
            </span>
          </div>
          <div
            className="h-1.5 rounded-full bg-paper overflow-hidden"
            data-testid="onboarding-progress-bar"
          >
            <div
              className={`h-full transition-all duration-500 ${
                showCelebration ? "bg-ochre" : "bg-forest"
              }`}
              style={{ width: `${data.percent}%` }}
            />
          </div>
        </div>

        {showCelebration ? (
          <div className="relative flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between pt-2">
            <div className="text-xs text-ink-light flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-forest" strokeWidth={2} />
              1 free month per signup, up to 12 months.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={dismiss}
                className="btn-secondary text-sm"
                data-testid="onboarding-later-btn"
              >
                Maybe later
              </button>
              <button
                type="button"
                onClick={openShare}
                className="btn-forest inline-flex items-center gap-1.5 text-sm"
                data-testid="onboarding-invite-btn"
              >
                <Gift className="w-4 h-4" strokeWidth={1.75} />
                Invite a teammate
              </button>
            </div>
          </div>
        ) : (
          <ul
            className="space-y-2 relative"
            data-testid="onboarding-steps-list"
          >
            {data.steps.map((s) => (
              <li key={s.id} data-testid={`onboarding-step-${s.id}`}>
                <Link
                  to={s.to}
                  className={`flex items-center gap-3 p-2.5 -mx-2.5 rounded-md transition-colors ${
                    s.done
                      ? "text-ink-light hover:bg-paper/60"
                      : "text-ink hover:bg-paper"
                  }`}
                >
                  {s.done ? (
                    <CheckCircle2
                      className="w-4 h-4 text-forest shrink-0"
                      strokeWidth={2}
                      data-testid={`onboarding-step-${s.id}-done`}
                    />
                  ) : (
                    <Circle
                      className="w-4 h-4 text-ink-lighter shrink-0"
                      strokeWidth={1.75}
                    />
                  )}
                  <span
                    className={`text-sm flex-1 ${
                      s.done ? "line-through decoration-forest/40" : ""
                    }`}
                  >
                    {s.label}
                  </span>
                  {!s.done && nextStep && nextStep.id === s.id && (
                    <span
                      className="text-xs font-medium text-forest flex items-center gap-1 shrink-0"
                      data-testid="onboarding-next-badge"
                    >
                      Next
                      <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ShareTaxSavingsModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setHidden(true);
          // Server-side persist so the celebration doesn't return on refresh.
          api.post("/dashboard/onboarding/dismiss").catch(() => {});
        }}
        taxSaved={taxSaved}
      />
    </>
  );
}
